import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListChecks,
  Loader2,
  Palette,
  Pencil,
  Plus,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Store,
  X,
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';

interface ScheduleRule {
  id?: string;
  shopId: string;
  ruleType: 'DEFAULT' | 'PIERCING' | 'TATTOO' | 'CONSULTATION';
  slotSizeMinutes: number;
  minorSlotSizeMinutes: number;
  maxAdvanceBookingDays: number;
  minAdvanceBookingHours: number;
  cancellationDeadlineHours: number;
  bufferTimeMinutes: number;
  lastReservationCutoffMinutes: number;
  piercingMinGapHours: number;
  nbPractitionerPerSlot: number;
  onlineSlotCapacity?: number | null;
  backofficeSlotCapacity?: number | null;
  walkInSlotCapacity?: number | null;
  slotSizeAdditionalActMinutes: number;
  planningRecommendation?: string | null;
  isActive: boolean;
}

interface Shop {
  id: string;
  name: string;
  timezone: string;
  defaultSlotSizeMinutes: number;
}

interface RuleTemplate {
  name: string;
  description: string;
  rules: Partial<ScheduleRule>;
}

const RULE_TYPES = ['DEFAULT', 'PIERCING', 'TATTOO', 'CONSULTATION'] as const;

const ruleTypeIcons = {
  DEFAULT: SlidersHorizontal,
  PIERCING: Sparkles,
  TATTOO: Palette,
  CONSULTATION: ListChecks,
} as const;

export function ScheduleRulesPage() {
  const { t } = useTranslation();
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [rules, setRules] = useState<ScheduleRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ScheduleRule | null>(null);

  const ruleTemplates: RuleTemplate[] = [
    {
      name: 'Standard Piercing',
      description: 'Default settings for piercing appointments',
      rules: {
        slotSizeMinutes: 15,
        minorSlotSizeMinutes: 30,
        maxAdvanceBookingDays: 90,
        minAdvanceBookingHours: 2,
        cancellationDeadlineHours: 24,
        bufferTimeMinutes: 15,
        lastReservationCutoffMinutes: 30,
        piercingMinGapHours: 24,
        nbPractitionerPerSlot: 1,
        onlineSlotCapacity: 1,
        backofficeSlotCapacity: null,
        walkInSlotCapacity: null,
        slotSizeAdditionalActMinutes: 15
      }
    },
    {
      name: 'Tattoo Sessions',
      description: 'Settings for longer tattoo sessions',
      rules: {
        slotSizeMinutes: 30,
        minorSlotSizeMinutes: 60,
        maxAdvanceBookingDays: 180,
        minAdvanceBookingHours: 4,
        cancellationDeadlineHours: 48,
        bufferTimeMinutes: 30,
        lastReservationCutoffMinutes: 30,
        piercingMinGapHours: 24,
        nbPractitionerPerSlot: 1,
        slotSizeAdditionalActMinutes: 30
      }
    },
    {
      name: 'Consultations',
      description: 'Quick consultation appointments',
      rules: {
        slotSizeMinutes: 10,
        minorSlotSizeMinutes: 15,
        maxAdvanceBookingDays: 30,
        minAdvanceBookingHours: 1,
        cancellationDeadlineHours: 12,
        bufferTimeMinutes: 5,
        lastReservationCutoffMinutes: 30,
        piercingMinGapHours: 24,
        nbPractitionerPerSlot: 1,
        slotSizeAdditionalActMinutes: 10
      }
    }
  ];

  useEffect(() => {
    loadShops();
  }, []);

  useEffect(() => {
    if (selectedShop) {
      loadScheduleRules(selectedShop.id);
    }
  }, [selectedShop]);

  const loadShops = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/shops?limit=1000&allShops=true'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch shops');
      }
      
      const data = await response.json();
      if (data.success) {
        const sortedShops = [...(data.data.shops || [])].sort((a: Shop, b: Shop) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setShops(sortedShops);
        if (sortedShops.length > 0) {
          setSelectedShop(sortedShops[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load shops:', error);
    }
  };

  const loadScheduleRules = async (shopId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/planning/schedule-rules/shop/${shopId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch schedule rules');
      }
      
      const data = await response.json();
      if (data.success) {
        setRules(data.data.rules || []);
      }
    } catch (error) {
      console.error('Failed to load schedule rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async (rule: ScheduleRule) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/planning/schedule-rules/shop/${rule.shopId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rules: [rule] })
      });

      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        setEditingRule(null);
        loadScheduleRules(rule.shopId);
      } else {
        alert(data.message || 'Failed to save schedule rule');
      }
    } catch (error) {
      console.error('Failed to save schedule rule:', error);
      alert('Failed to save schedule rule');
    }
  };

  const handleApplyTemplate = (template: RuleTemplate, ruleType: string) => {
    if (!selectedShop) return;

    const newRule: ScheduleRule = {
      shopId: selectedShop.id,
      ruleType: ruleType as any,
      slotSizeMinutes: template.rules.slotSizeMinutes ?? 15,
      minorSlotSizeMinutes: template.rules.minorSlotSizeMinutes ?? 30,
      maxAdvanceBookingDays: template.rules.maxAdvanceBookingDays ?? 90,
      minAdvanceBookingHours: template.rules.minAdvanceBookingHours ?? 2,
      cancellationDeadlineHours: template.rules.cancellationDeadlineHours ?? 24,
      bufferTimeMinutes: template.rules.bufferTimeMinutes ?? 15,
      lastReservationCutoffMinutes: template.rules.lastReservationCutoffMinutes ?? 30,
      piercingMinGapHours: template.rules.piercingMinGapHours ?? 24,
      nbPractitionerPerSlot: template.rules.nbPractitionerPerSlot ?? 1,
      onlineSlotCapacity: template.rules.onlineSlotCapacity === undefined ? 1 : template.rules.onlineSlotCapacity,
      backofficeSlotCapacity: template.rules.backofficeSlotCapacity ?? null,
      walkInSlotCapacity: template.rules.walkInSlotCapacity ?? null,
      slotSizeAdditionalActMinutes: template.rules.slotSizeAdditionalActMinutes ?? (template.rules.slotSizeMinutes ?? 15),
      isActive: true
    };

    setEditingRule(newRule);
    setShowEditModal(true);
  };

  const getRuleTypeLabel = (ruleType: string) => {
    const labels: { [key: string]: string } = {
      'DEFAULT': t('planning.scheduleRules.types.default'),
      'PIERCING': t('planning.scheduleRules.types.piercing'),
      'TATTOO': t('planning.scheduleRules.types.tattoo'),
      'CONSULTATION': t('planning.scheduleRules.types.consultation')
    };
    return labels[ruleType] || ruleType;
  };

  const getRuleByType = (ruleType: string) => {
    return rules.find(rule => rule.ruleType === ruleType);
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="dt-list-toolbar flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <Settings className="w-6 h-6 text-primary-600" />
              <span>{t('planning.scheduleRules.title')}</span>
              <HelpIcon
                docPath="08-gestion-planning#08-3-regles"
                tooltip="Documentation : Règles de planification"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {t('planning.scheduleRules.subtitle')}
            </p>
          </div>
        </div>

        {/* Shop Selector */}
        <div className="card">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-textPrimary">
              <Store className="w-4 h-4 text-primary-600" />
              {t('planning.scheduleRules.selectShop')}
            </label>
            <select
              value={selectedShop?.id || ''}
              onChange={(e) => {
                const shop = shops.find(s => s.id === e.target.value);
                setSelectedShop(shop || null);
              }}
              className="input-field sm:max-w-sm"
            >
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>{shop.name}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedShop && (
          <>
            {/* Quick Templates */}
            <div className="card">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-semibold text-textPrimary">
                  {t('planning.scheduleRules.quickTemplates')}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ruleTemplates.map((template, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-gray-200 p-4 transition-all duration-200 hover:border-primary-300 hover:shadow-sm"
                  >
                    <h3 className="font-medium text-textPrimary mb-1">{template.name}</h3>
                    <p className="text-sm text-textSecondary mb-4">{template.description}</p>
                    <div className="space-y-2">
                      {RULE_TYPES.map(ruleType => (
                        <button
                          key={ruleType}
                          onClick={() => handleApplyTemplate(template, ruleType)}
                          className="w-full rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-200 hover:bg-primary-100"
                        >
                          {t('planning.scheduleRules.applyTo')} {getRuleTypeLabel(ruleType)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Current Rules */}
            <div className="card">
              <div className="flex flex-col gap-1 border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-lg font-semibold text-textPrimary">
                  {t('planning.scheduleRules.currentRules')}
                </h2>
                <p className="text-sm text-textSecondary">{selectedShop.name}</p>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {RULE_TYPES.map(ruleType => {
                      const rule = getRuleByType(ruleType);
                      const RuleIcon = ruleTypeIcons[ruleType];
                      return (
                        <div
                          key={ruleType}
                          className="flex flex-col rounded-lg border border-gray-200 p-4 transition-all duration-200 hover:border-primary-300 hover:shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-2 mb-4">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="flex items-center justify-center w-9 h-9 shrink-0 bg-primary-100 rounded-lg">
                                <RuleIcon className="w-4 h-4 text-primary-600" />
                              </div>
                              <h3 className="font-medium text-textPrimary truncate">
                                {getRuleTypeLabel(ruleType)}
                              </h3>
                            </div>
                            <button
                              onClick={() => {
                                if (rule) {
                                  setEditingRule({
                                    ...rule,
                                    piercingMinGapHours: rule.piercingMinGapHours ?? 24,
                                    lastReservationCutoffMinutes: rule.lastReservationCutoffMinutes ?? 0,
                                    nbPractitionerPerSlot: rule.nbPractitionerPerSlot ?? 1,
                                    onlineSlotCapacity: rule.onlineSlotCapacity === undefined ? 1 : rule.onlineSlotCapacity,
                                    backofficeSlotCapacity: rule.backofficeSlotCapacity ?? null,
                                    walkInSlotCapacity: rule.walkInSlotCapacity ?? null,
                                    slotSizeAdditionalActMinutes: rule.slotSizeAdditionalActMinutes ?? rule.slotSizeMinutes ?? 15
                                  });
                                } else {
                                  setEditingRule({
                                    shopId: selectedShop.id,
                                    ruleType: ruleType as any,
                                    slotSizeMinutes: selectedShop.defaultSlotSizeMinutes || 15,
                                    minorSlotSizeMinutes: (selectedShop.defaultSlotSizeMinutes || 15) * 2,
                                    maxAdvanceBookingDays: 90,
                                    minAdvanceBookingHours: 2,
                                    cancellationDeadlineHours: 24,
                                    bufferTimeMinutes: 15,
                                    lastReservationCutoffMinutes: 30,
                                    piercingMinGapHours: 24,
                                    nbPractitionerPerSlot: 1,
                                    onlineSlotCapacity: ruleType === 'PIERCING' ? 1 : null,
                                    backofficeSlotCapacity: null,
                                    walkInSlotCapacity: null,
                                    slotSizeAdditionalActMinutes: selectedShop.defaultSlotSizeMinutes || 15,
                                    isActive: true
                                  });
                                }
                                setShowEditModal(true);
                              }}
                              className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                            >
                              {rule ? (
                                <>
                                  <Pencil className="w-3.5 h-3.5" />
                                  {t('planning.scheduleRules.edit')}
                                </>
                              ) : (
                                <>
                                  <Plus className="w-3.5 h-3.5" />
                                  {t('planning.scheduleRules.create')}
                                </>
                              )}
                            </button>
                          </div>

                          {rule ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.slotSize')}:</span>
                                <span className="font-medium text-textPrimary">{rule.slotSizeMinutes} min</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.minorSlotSize')}:</span>
                                <span className="font-medium text-textPrimary">{rule.minorSlotSizeMinutes} min</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.maxAdvance')}:</span>
                                <span className="font-medium text-textPrimary">{rule.maxAdvanceBookingDays} days</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.minAdvance')}:</span>
                                <span className="font-medium text-textPrimary">{rule.minAdvanceBookingHours} hours</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.cancellationDeadline')}:</span>
                                <span className="font-medium text-textPrimary">{rule.cancellationDeadlineHours} hours</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.bufferTime')}:</span>
                                <span className="font-medium text-textPrimary">{rule.bufferTimeMinutes} min</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">{t('planning.scheduleRules.lastReservationCutoff')}:</span>
                                <span className="font-medium text-textPrimary">{rule.lastReservationCutoffMinutes || 0} min</span>
                              </div>
                              {ruleType === 'PIERCING' ? (
                                <>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-textSecondary">Capacité en ligne:</span>
                                    <span className="font-medium text-textPrimary">{rule.onlineSlotCapacity ?? 'Illimité'}</span>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-textSecondary">Capacité backoffice:</span>
                                    <span className="font-medium text-textPrimary">{rule.backofficeSlotCapacity ?? 'Illimité'}</span>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-textSecondary">Capacité sans RDV:</span>
                                    <span className="font-medium text-textPrimary">{rule.walkInSlotCapacity ?? 'Illimité'}</span>
                                  </div>
                                </>
                              ) : (
                                <div className="flex justify-between gap-2">
                                  <span className="text-textSecondary">Capacité/créneau:</span>
                                  <span className="font-medium text-textPrimary">{rule.nbPractitionerPerSlot || 1}</span>
                                </div>
                              )}
                              <div className="flex justify-between gap-2">
                                <span className="text-textSecondary">Durée acte suppl.:</span>
                                <span className="font-medium text-textPrimary">{rule.slotSizeAdditionalActMinutes || rule.slotSizeMinutes} min</span>
                              </div>
                              {ruleType === 'PIERCING' && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-textSecondary">Délai min entre piercings:</span>
                                  <span className="font-medium text-textPrimary">{rule.piercingMinGapHours} heures</span>
                                </div>
                              )}
                              <div className="pt-1">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  rule.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {rule.isActive ? t('planning.scheduleRules.active') : t('planning.scheduleRules.inactive')}
                                </span>
                              </div>
                              {rule.planningRecommendation && (
                                <div className="mt-2 rounded-lg border border-primary-200 bg-primary-50 p-3">
                                  <p className="text-xs font-semibold text-primary-800 mb-1">
                                    Recommandation planning
                                  </p>
                                  <div
                                    className="text-xs text-primary-900 max-h-24 overflow-y-auto [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                                    dangerouslySetInnerHTML={{ __html: rule.planningRecommendation }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-1 flex-col items-center justify-center py-6 text-center">
                              <Settings className="w-8 h-8 text-gray-300 mb-2" />
                              <p className="text-sm text-textSecondary">{t('planning.scheduleRules.noRules')}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Edit Modal */}
        {showEditModal && editingRule && (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-4 sm:items-center">
            <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-display font-semibold text-textPrimary">
                    {t('planning.scheduleRules.editRule')}
                  </h2>
                  <p className="mt-1 text-sm text-textSecondary truncate">
                    {getRuleTypeLabel(editingRule.ruleType)}
                    {selectedShop ? ` · ${selectedShop.name}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRule(null);
                  }}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                  aria-label={t('common.cancel')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('planning.scheduleRules.slotSize')}
                      </label>
                      <input
                        type="number"
                        value={editingRule.slotSizeMinutes}
                        onChange={(e) => setEditingRule({ ...editingRule, slotSizeMinutes: parseInt(e.target.value) })}
                        className="input-field"
                        min="5"
                        max="120"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('planning.scheduleRules.minorSlotSize')}
                      </label>
                      <input
                        type="number"
                        value={editingRule.minorSlotSizeMinutes}
                        onChange={(e) => setEditingRule({ ...editingRule, minorSlotSizeMinutes: parseInt(e.target.value) })}
                        className="input-field"
                        min="5"
                        max="240"
                      />
                    </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('planning.scheduleRules.maxAdvance')}
                    </label>
                    <input
                      type="number"
                      value={editingRule.maxAdvanceBookingDays}
                      onChange={(e) => setEditingRule({ ...editingRule, maxAdvanceBookingDays: parseInt(e.target.value) })}
                      className="input-field"
                      min="1"
                      max="365"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('planning.scheduleRules.minAdvance')}
                    </label>
                    <input
                      type="number"
                      value={editingRule.minAdvanceBookingHours}
                      onChange={(e) => setEditingRule({ ...editingRule, minAdvanceBookingHours: parseInt(e.target.value) })}
                      className="input-field"
                      min="0"
                      max="168"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('planning.scheduleRules.cancellationDeadline')}
                    </label>
                    <input
                      type="number"
                      value={editingRule.cancellationDeadlineHours}
                      onChange={(e) => setEditingRule({ ...editingRule, cancellationDeadlineHours: parseInt(e.target.value) })}
                      className="input-field"
                      min="0"
                      max="168"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('planning.scheduleRules.bufferTime')}
                    </label>
                    <input
                      type="number"
                      value={editingRule.bufferTimeMinutes}
                      onChange={(e) => setEditingRule({ ...editingRule, bufferTimeMinutes: parseInt(e.target.value) })}
                      className="input-field"
                      min="0"
                      max="60"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('planning.scheduleRules.lastReservationCutoff')}
                    </label>
                    <input
                      type="number"
                      value={editingRule.lastReservationCutoffMinutes}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setEditingRule({
                          ...editingRule,
                          lastReservationCutoffMinutes: Number.isFinite(parsed) ? Math.max(0, parsed) : 0
                        });
                      }}
                      className="input-field"
                      min="0"
                      max="240"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('planning.scheduleRules.lastReservationCutoffHelp')}
                    </p>
                  </div>

                  {editingRule.ruleType === 'PIERCING' ? (
                    <div className="sm:col-span-2 space-y-3">
                      <p className="text-sm font-medium text-textPrimary">
                        Places piercing par créneau et par source
                      </p>
                      <p className="text-xs text-gray-500">
                        Laissez vide pour ne pas limiter. Une réservation en ligne n’occupe le créneau que pour une autre réservation en ligne. Une réservation backoffice ou sans rendez-vous laisse le créneau disponible en ligne.
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {([
                          ['onlineSlotCapacity', 'En ligne'],
                          ['backofficeSlotCapacity', 'Backoffice'],
                          ['walkInSlotCapacity', 'Sans rendez-vous'],
                        ] as const).map(([field, label]) => (
                          <div key={field}>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                            <input
                              type="number"
                              value={editingRule[field] ?? ''}
                              onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10);
                                setEditingRule({
                                  ...editingRule,
                                  [field]: e.target.value === '' || !Number.isFinite(parsed) ? null : Math.max(1, parsed),
                                });
                              }}
                              className="input-field"
                              min="1"
                              max="20"
                              placeholder="Illimité"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Capacité de réservation par créneau
                    </label>
                    <input
                      type="number"
                      value={editingRule.nbPractitionerPerSlot}
                      onChange={(e) => setEditingRule({ ...editingRule, nbPractitionerPerSlot: parseInt(e.target.value) || 1 })}
                      className="input-field"
                      min="1"
                      max="20"
                    />
                  </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Durée des actes supplémentaires (min)
                    </label>
                    <input
                      type="number"
                      value={editingRule.slotSizeAdditionalActMinutes}
                      onChange={(e) => setEditingRule({ ...editingRule, slotSizeAdditionalActMinutes: parseInt(e.target.value) || editingRule.slotSizeMinutes })}
                      className="input-field"
                      min="1"
                      max="240"
                    />
                  </div>

                  {editingRule.ruleType === 'PIERCING' && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Délai min entre piercings (heures)
                      </label>
                      <input
                        type="number"
                        value={editingRule.piercingMinGapHours}
                        onChange={(e) => setEditingRule({ ...editingRule, piercingMinGapHours: parseInt(e.target.value) })}
                        className="input-field"
                        min="0"
                        max="720"
                      />
                    </div>
                  )}

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Recommandation planning (optionnel)
                    </label>
                    <RichTextEditor
                      value={editingRule.planningRecommendation || ''}
                      onChange={(next) => setEditingRule({ ...editingRule, planningRecommendation: next })}
                      placeholder="Ex: Pour le piercing, privilégiez l'après-midi, idéalement entre 15h et 16h."
                    />
                  </div>

                  <div className="sm:col-span-2 flex items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={editingRule.isActive}
                      onChange={(e) => setEditingRule({ ...editingRule, isActive: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-300"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-textPrimary">
                      {t('planning.scheduleRules.active')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRule(null);
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveRule(editingRule)}
                  className="btn-primary"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
